import { useState } from "react";
import { PackageSearch, Search } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useMaster } from "@/lib/useMaster";
import { formatDateTime, formatIDR } from "@/lib/format";
import { Input } from "@/components/ui/input";

export default function StockCheck() {
  const { products } = useMaster();
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const filtered = products.filter(
    (p) =>
      !query ||
      p.name.toLowerCase().includes(query.toLowerCase()) ||
      p.sku.toLowerCase().includes(query.toLowerCase())
  );

  const check = async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stock/check?product_id=${id}`);
      setResult(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="stock-check-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-zinc-900">Stock Check</h1>
        <p className="mt-1 text-sm text-zinc-500">Look up a product's availability and FIFO batches across all warehouses.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="h-fit space-y-3 rounded-md border border-zinc-200 bg-white p-6 xl:sticky xl:top-20">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-400" />
            <Input
              data-testid="stock-check-search"
              placeholder="Search name or SKU..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="rounded-sm border-zinc-300 pl-9 focus-visible:ring-blue-600"
            />
          </div>
          <div className="max-h-[420px] space-y-1 overflow-y-auto">
            {filtered.length === 0 && (
              <p className="py-6 text-center text-sm text-zinc-400" data-testid="stock-check-no-products">No products found</p>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                data-testid={`stock-check-item-${p.sku}`}
                onClick={() => check(p.id)}
                className={`w-full rounded-sm border px-3 py-2.5 text-left transition-colors ${
                  result?.product.id === p.id
                    ? "border-blue-600 bg-blue-50"
                    : "border-zinc-200 hover:border-blue-300 hover:bg-zinc-50"
                }`}
              >
                <span className="block text-sm font-medium text-zinc-900">{p.name}</span>
                <span className="block font-mono text-xs text-zinc-500">{p.sku} · {p.category}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="xl:col-span-2">
          {loading && (
            <div className="grid h-40 place-items-center rounded-md border border-zinc-200 bg-white" data-testid="stock-check-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-300 border-t-blue-600" />
            </div>
          )}
          {!loading && !result && (
            <div className="grid h-40 place-items-center rounded-md border border-dashed border-zinc-300 bg-white" data-testid="stock-check-placeholder">
              <div className="text-center">
                <PackageSearch className="mx-auto h-8 w-8 text-zinc-300" />
                <p className="mt-2 text-sm text-zinc-400">Select a product to check its stock</p>
              </div>
            </div>
          )}
          {!loading && result && (
            <div className="space-y-4" data-testid="stock-check-result">
              <div className="flex flex-wrap items-center justify-between gap-4 rounded-md border border-zinc-200 bg-white p-6">
                <div>
                  <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-900" data-testid="stock-check-product-name">{result.product.name}</h2>
                  <p className="font-mono text-xs text-zinc-500">{result.product.sku} · {result.product.category}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-zinc-400">Total Available</p>
                  <p className="tnum text-2xl font-bold text-zinc-900" data-testid="stock-check-total">{result.total_qty} units</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {result.warehouses.map((w) => (
                  <div key={w.warehouse_id} className="rounded-md border border-zinc-200 bg-white p-6" data-testid={`stock-check-warehouse-${w.warehouse_code}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-heading text-base font-bold tracking-tight text-zinc-900">{w.warehouse_name}</p>
                        <p className="font-mono text-xs text-zinc-500">{w.warehouse_code}</p>
                      </div>
                      <p className={`tnum text-xl font-bold ${w.qty === 0 ? "text-zinc-300" : "text-zinc-900"}`} data-testid={`stock-check-qty-${w.warehouse_code}`}>
                        {w.qty} units
                      </p>
                    </div>
                    {w.batches.length === 0 ? (
                      <p className="mt-4 text-sm text-zinc-400">No stock in this warehouse</p>
                    ) : (
                      <table className="mt-4 w-full text-xs">
                        <thead>
                          <tr className="border-b border-zinc-200 text-left uppercase tracking-wide text-zinc-400">
                            <th className="py-2 pr-2 font-medium">Batch (oldest first)</th>
                            <th className="py-2 pr-2 font-medium">Date</th>
                            <th className="py-2 pr-2 text-right font-medium">Left</th>
                            <th className="py-2 text-right font-medium">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {w.batches.map((b) => (
                            <tr key={b.id} className="border-b border-zinc-100 last:border-0">
                              <td className="py-2 pr-2 font-mono text-zinc-600">
                                {b.batch_no}
                                {b.source === "transfer" && <span className="block text-[10px] text-blue-500">via {b.ref_no}</span>}
                              </td>
                              <td className="py-2 pr-2 text-zinc-500">{formatDateTime(b.created_at)}</td>
                              <td className="tnum py-2 pr-2 text-right font-semibold text-emerald-600">{b.qty_remaining}</td>
                              <td className="tnum py-2 text-right text-zinc-600">{formatIDR(b.purchase_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
